import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { generateCorrelationId } from "../../utils/common.ts";
import { getStoredToken } from "../../utils/sessionManager.ts";

// Enhanced base query with authentication and error handling
export const backendBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // const TOKEN_RETRY_ATTEMPTS = 3;
  // const TOKEN_RETRY_DELAY_MS = 50;
  // const INITIAL_DELAY_MS = 10; // Small delay to prevent race conditions
  // Determine if this is an OAuth callback
  const urlParams = new URLSearchParams(window.location.search);
  const isOAuthCallback = urlParams.has("code") && urlParams.has("state");

  // Determine if this is an API call that requires authentication
  const apiUrl = typeof args === "string" ? args : args.url;

  // Add a small delay to help prevent race conditions during token retrieval
  // await new Promise((resolve) => setTimeout(resolve, INITIAL_DELAY_MS));

  // Define truly public API endpoints (no authentication required)
  const publicApiEndpoints = ["/authorization", "/auth/callback"];
  const isPublicApiCall = publicApiEndpoints.some((endpoint) =>
    apiUrl.includes(endpoint),
  );
  // Pre-validate token availability before creating the request
  const authToken: string | null = getStoredToken();

  const URL = import.meta.env.VITE_BACKEND_API_URL;

  // Check if the request body is FormData
  const isFormDataRequest =
    typeof args === "object" && args.body instanceof FormData;

  // Create the base query function without retry wrapper
  const baseQueryFn = fetchBaseQuery({
    baseUrl: URL,

    prepareHeaders: (headers) => {
      // Only set Content-Type to application/json if:
      // 1. It's not already set
      // 2. It's not a FormData request (FormData needs browser to set Content-Type with boundary)
      if (!headers.has("Content-Type") && !isFormDataRequest) {
        headers.set("Content-Type", "application/json");
      }

      // Add authentication token for all requests except public API endpoints
      if (!isPublicApiCall && !isOAuthCallback && authToken) {
        headers.set("Authorization", `Bearer ${authToken}`);
      }
      return headers;
    },
  });

  // Automatically inject correlationId for POST/PUT/DELETE requests
  // Skip for FormData bodies (file uploads) as they handle correlationId separately
  if (
    typeof args === "object" &&
    (args.method === "POST" ||
      args.method === "PUT" ||
      args.method === "DELETE")
  ) {
    const correlationId = generateCorrelationId();

    // Skip correlationId injection for FormData (file uploads)
    const isFormData = args.body instanceof FormData;

    if (!args.body && !isFormData) {
      args.body = { correlationId };
    } else if (
      typeof args.body === "object" &&
      !("correlationId" in args.body) &&
      !isFormData
    ) {
      args.body = {
        correlationId,
        ...(args.body as Record<string, unknown>),
      };
    }
  }

  // Custom retry logic: only retry when HTTP status is empty, null, or undefined
  const maxRetries = 3;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Execute the base query
    const result = await baseQueryFn(args, api, extraOptions);

    // Get the HTTP status from the response meta
    const httpStatus = result.meta?.response?.status;

    // Only retry if HTTP status is null or undefined (network failures without a proper HTTP response)
    // Do NOT retry for actual HTTP status codes (200, 400, 500 series)
    const shouldRetry = httpStatus === null || httpStatus === undefined;

    // If we have a valid HTTP status, return immediately (success or error)
    if (!shouldRetry) {
      return result;
    }

    // If we've exhausted retries, return the result even without HTTP status
    if (attempt === maxRetries) {
      return result;
    }

    // Wait before retrying (exponential backoff)
    await new Promise((resolve) =>
      setTimeout(resolve, Math.pow(2, attempt) * 100),
    );
    continue;
  }

  // Fallback - execute one final time (should not reach here in normal flow)
  return await baseQueryFn(args, api, extraOptions);
};

// Define a service for real backend API endpoints
export const backendApi = createApi({
  reducerPath: "backendApi",
  baseQuery: backendBaseQuery,
  tagTypes: [
    "BackendContractParameter",
    "UserInfo",
    "BackendPriceRegions",
    "BackendFuelProducts",
    "BackendVatRates",
    "PartnersData",
    "ImportLog",
    "AllUsers",
    "LoggedInUser",
    "NonTraditionalPricingData",
    "AuxiliarySiteInfo",
    "BackendPlantDetails",
    "ContractParameterPerPartner",
    "ContractParameterPerPartnerActivityLog",
    "ContractParameterPerSite",
    "ContractParameterPerSiteActivityLog",
    "ContractParameterPerSiteProduct",
    "ContractParameterPerSiteProductActivityLog",
    "ReportStatusLog",
    "CPPartnerFileImportLog",
    "CPSiteFileImportLog",
    "CPSiteProductFileImportLog",
    "SiteMdmData",
    "SiteMdmImport",
    "CustomerSiteMapping",
    "ManualSalesCorrection",
    "GsapSalesRebates",
    "PlusCardExportLogs",
    "PlusCardImports",
    "PlusCardDetails",
    "PlusCardUniqueTransactions",
    "PlusCardActiveTransactions",
    "PlusCardAuditLog",
  ],
  endpoints: () => ({}),
});
